import * as _ from 'lodash-es';
import {TableColumn, TableColumnConfig, TableColumnsDefMap} from './table';

export function includeRequiredColumns(
    columnsDef: TableColumn[],
    columnsConfig: TableColumnConfig[]
): TableColumnConfig[] {
    const missedRequiredColumnsConfig: TableColumnConfig[] = columnsDef
        .filter(columnDef => columnDef.isRequired)
        .filter(columnDef => !columnsConfig.find(columnConfig => columnConfig.key === columnDef.key))
        .map(getDefaultColumnConfig);

    return sortedInsertColumnsConfig(columnsDef, columnsConfig, missedRequiredColumnsConfig);
}

export function fixGroupedColumns(
    columnsDef: TableColumn[],
    columnsConfig: TableColumnConfig[]
): TableColumnConfig[] {
    const columnsDefMap = createColumnsDict(columnsDef);
    const getGroupId = (columnConfig: TableColumnConfig) => columnsDefMap[columnConfig.key].groupId;
    const createGroupColumnsConfig = (groupId: any) => columnsDef
        .filter(columnDef => columnDef.groupId === groupId)
        .map(columnDef => ({key: columnDef.key}));

    return columnsConfig
        .reduce((groupingMeta, columnConfig) => {
            const groupId = getGroupId(columnConfig);
            const isGroupedColumn: boolean = groupId !== null && groupId !== undefined;

            if (isGroupedColumn) {
                let group: TableColumnConfig[] = groupingMeta.groupById.get(groupId);

                if (!group) {
                    group = createGroupColumnsConfig(groupId);
                    groupingMeta.groupById.set(groupId, group);
                    groupingMeta.groups.push(group);
                }

                const columnIndexInGroup = _.findIndex(
                    group,
                    currentColumnConfig => currentColumnConfig.key === columnConfig.key
                );

                group.splice(columnIndexInGroup, 1, columnConfig);
            } else {
                groupingMeta.groups.push([columnConfig]);
            }

            return groupingMeta;
        }, {
            groups: [],
            groupById: new Map()
        } as {
            groups: TableColumnConfig[][],
            groupById: Map<any, TableColumnConfig[]>
        }).groups
        .reduce((resultColumnsConfig, group) => resultColumnsConfig.concat(group), []);
}

export function removeDuplicatedColumns(columnsConfig: TableColumnConfig[]): TableColumnConfig[] {
    return _.uniqBy(columnsConfig, columnConfig => columnConfig.key);
}

export function getDefaultColumnConfig<TColDef extends TableColumn = TableColumn,
    TColConf extends TableColumnConfig = TableColumnConfig>(
    columnDef: TColDef
): TColConf {
    const columnConfig = {
        key: columnDef.key
    } as TColConf;

    if (!_.isNil(columnDef.width)) {
        columnConfig.width = columnDef.width;
    }

    return columnConfig;
}

export function applyColumnsDefaultSettings<TKey extends string = any>(
    columnsDef: Array<TableColumn<TKey>>,
    columnsConfig: Array<TableColumnConfig<TKey>>
): Array<TableColumnConfig<TKey>> {
    const columnsDefMap = createColumnsDict(columnsDef);

    return columnsConfig.map(columnConfig => _.defaults(
        {},
        columnConfig,
        getDefaultColumnConfig(columnsDefMap[columnConfig.key])
    ));
}

export function removeUnknownColumns(
    columnsDef: TableColumn[],
    columnsConfig: TableColumnConfig[]
): TableColumnConfig[] {
    const columnsDefMap = createColumnsDict(columnsDef);

    return columnsConfig.filter(columnConfig => {
        const columnDef = columnsDefMap[columnConfig.key];

        return columnDef && !columnDef.isHidden;
    });
}

export function normalizeColumnsConfig(
    columnsDef: TableColumn[],
    columnsConfig: TableColumnConfig[]
): TableColumnConfig[] {
    columnsConfig = removeUnknownColumns(columnsDef, columnsConfig);
    columnsConfig = removeDuplicatedColumns(columnsConfig);
    columnsConfig = includeRequiredColumns(columnsDef, columnsConfig);
    columnsConfig = fixGroupedColumns(columnsDef, columnsConfig);
    columnsConfig = applyColumnsDefaultSettings(columnsDef, columnsConfig);

    return columnsConfig;
}

export function getColumnDefByKey<TKey extends string = string, TColDef extends TableColumn = TableColumn>(
    columnsDef: TColDef[],
    columnsKey: TKey
): TColDef {
    return columnsDef.find(column => column.key === columnsKey);
}

export function createColumnsDict<TKey extends string = string, TValue extends TableColumn = TableColumn>(
    columnsDef: TValue[]
): TableColumnsDefMap<TKey, TValue> {
    return _.keyBy<TableColumn>(
        columnsDef,
        (columnDef: TableColumn) => columnDef.key
    ) as TableColumnsDefMap<TKey, TValue>;
}

export function getDefaultColumnsConfigByDef(columnsDef: TableColumn[]): TableColumnConfig[] {
    const columnsConfig: TableColumnConfig[] = columnsDef
        .filter(columnDef => !columnDef.isHiddenByDefault && !columnDef.isHidden)
        .map(getDefaultColumnConfig);

    return applyColumnsDefaultSettings(columnsDef, columnsConfig);
}

export function sortedIncludeColumnConfig<TColDef extends TableColumn = TableColumn,
    TColConf extends TableColumnConfig = TableColumnConfig>(
    columnsDef: TColDef[],
    columnsConfig: TColConf[],
    columnConfigToInsert: TColConf
): TColConf[] {
    let resultColumnsConfig = columnsConfig;

    const hasColumn = !!columnsConfig.find(columnConfig => columnConfig.key === columnConfigToInsert.key);

    if (!hasColumn) {
        const newIndex = _.sortedIndexBy(
            columnsConfig,
            columnConfigToInsert,
            columnConfig => columnsDef.findIndex(columnDef => columnDef.key === columnConfig.key)
        );

        resultColumnsConfig = [...columnsConfig];
        resultColumnsConfig.splice(newIndex, 0, columnConfigToInsert);
    }

    return resultColumnsConfig;
}

export function sortedInsertColumnsConfig<TKey extends string = string,
TColDef extends TableColumn = TableColumn,
TColConf extends TableColumnConfig = TableColumnConfig>(
    columnsDef: TColDef[],
    columnsConfig: TColConf[],
    columnsConfigToInsert: TColConf[]
): TableColumnConfig[] {
    return columnsConfigToInsert.reduce(
        (columnsConfigAcc, columnConfigToInsert) =>
            sortedIncludeColumnConfig(
                columnsDef,
                columnsConfigAcc,
                columnConfigToInsert
            ),
        columnsConfig
    );
}

export function sortedIncludeColumnConfigByKey<TKey extends string = string,
    TColDef extends TableColumn = TableColumn,
    TColConf extends TableColumnConfig = TableColumnConfig>(
    columnsDef: TColDef[],
    columnsConfig: TColConf[],
    columnKeyToInsert: TKey
): TColConf[] {
    const columnConfigToInsert = getDefaultColumnConfig<TColDef, TColConf>(
        getColumnDefByKey(columnsDef, columnKeyToInsert)
    );

    return sortedIncludeColumnConfig<TColDef, TColConf>(columnsDef, columnsConfig, columnConfigToInsert);
}
